import { useSeo, useJsonLd } from "@/lib/seo";

/**
 * Renders SEO tags + CollectionPage / BreadcrumbList / ItemList JSON-LD for
 * a category (or "all collections" if no slug).
 */
export default function CollectionSEO({ slug, section, title, subtitle, items }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const seg = slug ? `/collections/${slug}` : (section ? `/collections/${section}` : "/collections");
  const url = `${origin}${seg}`;

  const description = items?.length
    ? `${title} — ${items.length} hand-crafted couture pieces from BARIQUE, New Delhi. ${subtitle || ""}`.trim()
    : `${title} — Contemporary Indian couture from BARIQUE, New Delhi.`;

  const primaryImage = items?.[0]?.images?.[0] || items?.[0]?.image || "";

  // JSON-LD CollectionPage
  const collectionLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: title,
    description: description.slice(0, 300),
    url,
    isPartOf: { "@type": "WebSite", name: "BARIQUE", url: `${origin}/` },
    ...(primaryImage ? { image: primaryImage } : {}),
  };

  // JSON-LD ItemList of products
  const itemListLd = items?.length && {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: title,
    numberOfItems: items.length,
    itemListElement: items.slice(0, 50).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${origin}/product/${p.slug}`,
      name: p.title,
      image: (p.images?.[0] || p.image || undefined),
    })),
  };

  // JSON-LD BreadcrumbList
  const label = slug || section || "all";
  const crumbs = [
    { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
    { "@type": "ListItem", position: 2, name: "Collections", item: `${origin}/collections` },
  ];
  if (slug || section) {
    crumbs.push({
      "@type": "ListItem", position: 3,
      name: title,
      item: `${origin}/collections/${label}`,
    });
  }
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs,
  };

  useSeo({
    title: `${title} · BARIQUE`,
    description: description.slice(0, 160),
    canonical: url,
    image: primaryImage,
    type: "website",
  });

  useJsonLd([collectionLd, itemListLd, breadcrumbLd].filter(Boolean));

  return null;
}
